import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../api/Authcontext';
import { adminCreateLoan, searchUsers } from '../../api/Index';
import './CreateLoan.css';
import PageTitle from '../../components/UI/PageTitle';

// Simple debounce implementation
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

const CreateLoan = () => {
  const navigate = useNavigate();
  const { userToken } = useAuth();
  
  // Form state
  const [loanData, setLoanData] = useState({
    amount: '',
    purpose: '',
    repaymentPeriod: ''
  });
  
  // Borrower selection
  const [selectedBorrower, setSelectedBorrower] = useState(null);
  const [borrowerSearchQuery, setBorrowerSearchQuery] = useState('');
  const [borrowerResults, setBorrowerResults] = useState([]);
  const [showBorrowerModal, setShowBorrowerModal] = useState(false);
  const [searchingBorrowers, setSearchingBorrowers] = useState(false);
  
  // Guarantor selection
  const [selectedGuarantors, setSelectedGuarantors] = useState([]);
  const [guarantorSearchQuery, setGuarantorSearchQuery] = useState('');
  const [guarantorResults, setGuarantorResults] = useState([]);
  const [showGuarantorModal, setShowGuarantorModal] = useState(false);
  const [searchingGuarantors, setSearchingGuarantors] = useState(false);
  
  // Submission state
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  
  // Search functions
  const searchBorrowers = async (query = '') => {
    setSearchingBorrowers(true);
    try {
      const response = await searchUsers(query);
      const users = response.data || [];
      // Filter out selected borrower and guarantors
      const excludeIds = [
        ...(selectedBorrower ? [selectedBorrower._id] : []),
        ...selectedGuarantors.map(g => g._id)
      ];
      const filteredUsers = users.filter(user => !excludeIds.includes(user._id));
      setBorrowerResults(filteredUsers);
    } catch (error) {
      console.error('Error searching borrowers:', error);
    } finally {
      setSearchingBorrowers(false);
    }
  };
  
  const searchGuarantors = async (query = '') => {
    setSearchingGuarantors(true);
    try {
      const response = await searchUsers(query);
      const users = response.data || [];
      // Filter out selected borrower and guarantors
      const excludeIds = [
        ...(selectedBorrower ? [selectedBorrower._id] : []),
        ...selectedGuarantors.map(g => g._id)
      ];
      const filteredUsers = users.filter(user => !excludeIds.includes(user._id));
      setGuarantorResults(filteredUsers);
    } catch (error) {
      console.error('Error searching guarantors:', error);
    } finally {
      setSearchingGuarantors(false);
    }
  };
  
  // Debounced search functions
  const debouncedBorrowerSearch = useCallback(
    debounce((query) => searchBorrowers(query), 300),
    [selectedBorrower, selectedGuarantors]
  );
  
  const debouncedGuarantorSearch = useCallback(
    debounce((query) => searchGuarantors(query), 300),
    [selectedBorrower, selectedGuarantors]
  );
  
  // Modal handlers
  const openBorrowerModal = () => {
    setShowBorrowerModal(true);
    setBorrowerSearchQuery('');
    searchBorrowers('');
  };
  
  const openGuarantorModal = () => {
    if (selectedGuarantors.length >= 3) {
      alert('Maximum 3 guarantors allowed');
      return;
    }
    setShowGuarantorModal(true);
    setGuarantorSearchQuery('');
    searchGuarantors('');
  };
  
  // Selection handlers
  const selectBorrower = (user) => {
    setSelectedBorrower(user);
    setShowBorrowerModal(false);
  };
  
  const addGuarantor = (user) => {
    const newGuarantor = {
      ...user,
      legacyCreditsUsed: ''
    };
    setSelectedGuarantors([...selectedGuarantors, newGuarantor]);
    setShowGuarantorModal(false);
  };
  
  const removeGuarantor = (guarantorId) => {
    setSelectedGuarantors(selectedGuarantors.filter(g => g._id !== guarantorId));
  };
  
  const updateGuarantorCredits = (guarantorId, credits) => {
    setSelectedGuarantors(selectedGuarantors.map(g => 
      g._id === guarantorId ? { ...g, legacyCreditsUsed: credits } : g
    ));
  };
  
  // Form validation
  const validateForm = () => {
    if (!selectedBorrower) {
      setError('Please select a loan applicant');
      return false;
    }
    
    if (!loanData.amount || isNaN(parseFloat(loanData.amount)) || parseFloat(loanData.amount) <= 0) {
      setError('Please enter a valid loan amount');
      return false;
    }
    
    if (!loanData.purpose.trim()) {
      setError('Please enter loan purpose');
      return false;
    }
    
    if (!loanData.repaymentPeriod || isNaN(parseInt(loanData.repaymentPeriod)) || parseInt(loanData.repaymentPeriod) <= 0) {
      setError('Please enter a valid repayment period');
      return false;
    }
    
    if (selectedGuarantors.length === 0) {
      setError('Please add at least one guarantor');
      return false;
    }
    
    // Validate guarantor credits
    for (const guarantor of selectedGuarantors) {
      if (!guarantor.legacyCreditsUsed || isNaN(parseInt(guarantor.legacyCreditsUsed)) || parseInt(guarantor.legacyCreditsUsed) <= 0) {
        setError(`Please enter valid legacy credits for ${guarantor.name}`);
        return false;
      }
    }
    
    return true;
  };
  
  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!validateForm()) return;
    
    setCreating(true);
    try {
      const guarantors = selectedGuarantors.map(g => ({
        guarantorId: g._id,
        legacyCreditsUsed: parseInt(g.legacyCreditsUsed)
      }));
      
      const loanPayload = {
        userId: selectedBorrower._id,
        amount: parseFloat(loanData.amount),
        purpose: loanData.purpose,
        repaymentPeriod: parseInt(loanData.repaymentPeriod),
        guarantors
      };
      
      const response = await adminCreateLoan(loanPayload);
      
      // Redirect to admin loans page
      navigate('/admin-loans');
      
    } catch (err) {
      console.error('Error creating loan:', err);
      setError(err.message || 'Failed to create loan');
      setCreating(false);
    }
  };
  
  return (
    <div>
        <PageTitle title='Create Loan' />
        <div className="create-loan-container">
            <div className="form-container">
                {error && (
                    <div style={{
                        backgroundColor: '#ffe6e6',
                        border: '1px solid #ff0000',
                        borderRadius: '4px',
                        padding: '10px',
                        marginBottom: '20px',
                        color: '#cc0000'
                    }}>
                        {error}
                    </div>
                )}
                
                <form className="create-loan-form" onSubmit={handleSubmit}>
                    {/* Borrower Selection */}
                    <div className="input-group">
                        <label className="input-label">Loan Applicant</label>
                        <div
                            className="input selection-input"
                            onClick={openBorrowerModal}
                            style={{
                                cursor: 'pointer',
                                backgroundColor: selectedBorrower ? '#f0f8ff' : '#fff',
                                border: selectedBorrower ? '2px solid #2196f3' : '1px solid #ddd'
                            }}
                        >
                            {selectedBorrower ? (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <div style={{ fontWeight: 'bold' }}>{selectedBorrower.name}</div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>{selectedBorrower.email}</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedBorrower(null);
                                        }}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#ff0000',
                                            cursor: 'pointer',
                                            fontSize: '16px'
                                        }}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ) : (
                                'Tap to select Loan Applicant'
                            )}
                        </div>
                    </div>

                    <div className="input-group">
                        <label className="input-label">Loan Amount (KES)</label>
                        <input
                            type="number"
                            className="input"
                            value={loanData.amount}
                            onChange={(e) => setLoanData({...loanData, amount: e.target.value})}
                            placeholder="Enter loan amount"
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Loan Purpose</label>
                        <textarea
                            className="text-area"
                            value={loanData.purpose}
                            onChange={(e) => setLoanData({...loanData, purpose: e.target.value})}
                            placeholder="Enter Loan Purpose"
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Repayment Period (months)</label>
                        <input
                            type="number"
                            className="input"
                            value={loanData.repaymentPeriod}
                            onChange={(e) => setLoanData({...loanData, repaymentPeriod: e.target.value})}
                            placeholder="Enter Repayment period"
                            required
                        />
                    </div>

                    {/* Guarantors Section */}
                    <div className="input-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <h4 style={{ margin: 0 }}>LOAN GUARANTORS</h4>
                            <span style={{ fontSize: '14px', color: '#666' }}>({selectedGuarantors.length}/3)</span>
                        </div>
                        
                        {/* Add Guarantor Button */}
                        {selectedGuarantors.length < 3 && (
                            <button
                                type="button"
                                className="form-button"
                                onClick={openGuarantorModal}
                                style={{
                                    backgroundColor: '#28a745',
                                    marginBottom: '15px'
                                }}
                            >
                                ➕ Add Guarantor
                            </button>
                        )}
                        
                        {/* Selected Guarantors */}
                        {selectedGuarantors.map((guarantor, index) => (
                            <div
                                key={guarantor._id}
                                style={{
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    padding: '15px',
                                    marginBottom: '10px',
                                    backgroundColor: '#f9f9f9'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <label className="input-label">{`Guarantor ${index + 1}`}</label>
                                        <div style={{ marginBottom: '10px' }}>
                                            <div style={{ fontWeight: 'bold' }}>{guarantor.name}</div>
                                            <div style={{ fontSize: '12px', color: '#666' }}>{guarantor.email}</div>
                                        </div>
                                        <input
                                            type="number"
                                            className="input"
                                            value={guarantor.legacyCreditsUsed}
                                            onChange={(e) => updateGuarantorCredits(guarantor._id, e.target.value)}
                                            placeholder="Legacy Credits used"
                                            required
                                            style={{ marginTop: '5px' }}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeGuarantor(guarantor._id)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#ff0000',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            marginLeft: '10px'
                                        }}
                                    >
                                        ✕
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <button 
                        type="submit" 
                        className='form-button'
                        disabled={creating}
                        style={{
                            opacity: creating ? 0.6 : 1,
                            cursor: creating ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {creating ? 'Creating Loan...' : 'Create Loan'}
                    </button>
                </form>
            </div>
        </div>
        
        {/* Borrower Search Modal */}
        {showBorrowerModal && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '20px',
                    maxWidth: '500px',
                    width: '90%',
                    maxHeight: '70vh',
                    overflow: 'auto'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3 style={{ margin: 0 }}>Select Loan Applicant</h3>
                        <button
                            onClick={() => setShowBorrowerModal(false)}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '20px',
                                cursor: 'pointer'
                            }}
                        >
                            ✕
                        </button>
                    </div>
                    
                    <input
                        type="text"
                        className="input"
                        value={borrowerSearchQuery}
                        onChange={(e) => {
                            setBorrowerSearchQuery(e.target.value);
                            debouncedBorrowerSearch(e.target.value);
                        }}
                        placeholder="Search for members..."
                        style={{ marginBottom: '15px' }}
                    />
                    
                    {searchingBorrowers && (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            Searching...
                        </div>
                    )}
                    
                    <div>
                        {!searchingBorrowers && borrowerResults.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                {borrowerSearchQuery ? 'No members found' : 'Start typing to search'}
                            </div>
                        )}
                        {borrowerResults.map(user => (
                            <div
                                key={user._id}
                                onClick={() => selectBorrower(user)}
                                style={{
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    marginBottom: '5px',
                                    cursor: 'pointer',
                                    ':hover': { backgroundColor: '#f5f5f5' }
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                            >
                                <div style={{ fontWeight: 'bold' }}>{user.name}</div>
                                <div style={{ fontSize: '12px', color: '#666' }}>{user.email}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )}
        
        {/* Guarantor Search Modal */}
        {showGuarantorModal && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '20px',
                    maxWidth: '500px',
                    width: '90%',
                    maxHeight: '70vh',
                    overflow: 'auto'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3 style={{ margin: 0 }}>Add Guarantor</h3>
                        <button
                            onClick={() => setShowGuarantorModal(false)}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '20px',
                                cursor: 'pointer'
                            }}
                        >
                            ✕
                        </button>
                    </div>
                    
                    <input
                        type="text"
                        className="input"
                        value={guarantorSearchQuery}
                        onChange={(e) => {
                            setGuarantorSearchQuery(e.target.value);
                            debouncedGuarantorSearch(e.target.value);
                        }}
                        placeholder="Search for guarantors..."
                        style={{ marginBottom: '15px' }}
                    />
                    
                    {searchingGuarantors && (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            Searching...
                        </div>
                    )}
                    
                    <div>
                        {!searchingGuarantors && guarantorResults.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                {guarantorSearchQuery ? 'No members found' : 'Start typing to search'}
                            </div>
                        )}
                        {guarantorResults.map(user => (
                            <div
                                key={user._id}
                                onClick={() => addGuarantor(user)}
                                style={{
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    marginBottom: '5px',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                            >
                                <div style={{ fontWeight: 'bold' }}>{user.name}</div>
                                <div style={{ fontSize: '12px', color: '#666' }}>{user.email}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )}
    </div>
  );
};

export default CreateLoan;